import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useState } from "react";
import AdGoalFields from "./AdGoalFields";
import AdImagePicker from "./AdImagePicker";
import AdvancedOptions from "./AdvancedOptions";
import DeliveryFields from "./DeliveryFields";
import { AD_GOALS, currencyOf } from "./marketingFormat";
import { useAdForm } from "./useAdForm";
import { useDeliveryPlan } from "./useDeliveryPlan";

export default function CreateAdDialog({ connections, defaultConnectionId, onClose, actions }) {
  const [chosen, setChosen] = useState(null);
  const connectionId = chosen ?? defaultConnectionId ?? connections[0]?.id ?? "";
  const connection = connections.find((c) => c.id === connectionId);
  const currency = currencyOf(connections, connectionId);
  const [imageHash, setImageHash] = useState(null);
  const form = useAdForm();
  const plan = useDeliveryPlan();
  const { createAd, uploadImage } = actions;
  const { fields, set } = form;
  const canUseInstagram = Boolean(connection?.instagramUserId) && fields.goal !== "MESSAGES";

  const changeAccount = (id) => {
    setChosen(id);
    setImageHash(null);
    set("instagram")(false);
    form.resetInterests();
  };

  const upload = (file) => {
    setImageHash(null);
    uploadImage.mutate(
      { connectionId, file },
      { onSuccess: (res) => setImageHash(res?.data?.data?.imageHash) }
    );
  };

  const valid = Boolean(connection) && Boolean(imageHash) && form.valid && plan.valid;

  const submit = () =>
    createAd.mutate(
      {
        connectionId,
        imageHash,
        instagram: fields.instagram && canUseInstagram,
        ...form.payload,
        ...plan.payload,
      },
      { onSuccess: onClose }
    );

  return (
    <Dialog
      open
      onClose={createAd.isLoading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <DialogTitle>Create an ad</DialogTitle>
      <DialogContent className="space-y-3">
        <p className="pt-2 text-sm text-app-text-secondary">
          Makes a new ad from an image and text. It is not published on your Facebook page or
          Instagram feed and only shows as an ad. To promote a post that already exists, boost it
          from the Posts tab instead.
        </p>
        {connections.length > 1 ? (
          <TextField
            select
            label="Account"
            value={connectionId}
            onChange={(e) => changeAccount(e.target.value)}
            fullWidth
            size="small"
          >
            {connections.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <TextField
          label="Ad name"
          value={fields.name}
          onChange={(e) => set("name")(e.target.value)}
          fullWidth
          size="small"
          helperText="Only you see this, in the campaign list"
        />
        <AdImagePicker uploading={uploadImage.isLoading} hash={imageHash} onPick={upload} />
        <TextField
          label="Ad text"
          value={fields.message}
          onChange={(e) => set("message")(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          size="small"
          error={fields.message.length > form.maxMessage}
          helperText={`${fields.message.length}/${form.maxMessage}`}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={fields.goal}
          onChange={(_, next) => next && form.changeGoal(next)}
          aria-label="Ad goal"
        >
          {AD_GOALS.map((item) => (
            <ToggleButton key={item.value} value={item.value}>
              {item.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <AdGoalFields form={form} />
        {canUseInstagram ? (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={fields.instagram}
                onChange={(e) => set("instagram")(e.target.checked)}
              />
            }
            label="Also show on Instagram"
          />
        ) : null}
        <DeliveryFields plan={plan} currency={currency} subject="ad" />
        <AdvancedOptions form={form} connectionId={connectionId} currency={currency} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createAd.isLoading}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!valid || createAd.isLoading} onClick={submit}>
          {createAd.isLoading ? "Creating…" : "Create paused ad"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
